import React, { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Server, Globe, Smartphone, RefreshCw, CheckCircle, XCircle, HelpCircle, Loader2, Activity } from 'lucide-react';
import { getPlatformHealth, type ServiceHealth } from '../../services/platformAnalyticsService';

const dot = (status: string) =>
  status === 'up' ? 'bg-success' : status === 'down' ? 'bg-danger' : 'bg-muted-foreground';

const CATEGORY_META: Record<string, { title: string; icon: React.ReactNode }> = {
  backend: { title: 'Backend services', icon: <Server className="w-4 h-4" /> },
  frontend: { title: 'Web applications', icon: <Globe className="w-4 h-4" /> },
  mobile: { title: 'Mobile', icon: <Smartphone className="w-4 h-4" /> },
};
const CATEGORY_ORDER = ['backend', 'frontend', 'mobile'];

// Compact version label: show the deployment/ref tail (e.g. FLY_IMAGE_REF
// "…:deployment-01H…" → "01H…") or a short git SHA. '—' when unreported.
const shortVer = (v?: string | null): string => {
  if (!v) return '';
  const tail = v.includes(':') ? v.split(':').pop()! : v;
  return tail.replace(/^deployment-/, '').slice(0, 12);
};

export const MonitoringPage: React.FC = () => {
  const [services, setServices] = useState<ServiceHealth[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);

  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await getPlatformHealth();
      setServices(Array.isArray(res?.services) ? res.services : []);
      setCheckedAt(new Date());
    } catch {
      if (initial) message.error('Could not load service health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load(true);
    const id = setInterval(() => void load(false), 15000); // auto-refresh
    return () => clearInterval(id);
  }, [load]);

  const upCount = services.filter((s) => s.status === 'up').length;
  const downCount = services.filter((s) => s.status === 'down').length;
  const probedCount = services.filter((s) => s.status !== 'unknown').length;
  // "Healthy" = nothing is actually down; unknown (e.g. mobile, not probed) is neutral.
  const allUp = services.length > 0 && downCount === 0;
  const groups = CATEGORY_ORDER
    .map((cat) => ({ cat, items: services.filter((s) => (s.category ?? 'backend') === cat) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1">
          <h1 className="text-h2 text-foreground">System Monitoring</h1>
          <p className="text-muted-foreground mt-1">
            Live health of platform services
            {checkedAt && <> · checked {checkedAt.toLocaleTimeString()}</>}
          </p>
        </div>
        <Button variant="outline" onClick={() => void load(false)} disabled={refreshing}>
          <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {!loading && services.length > 0 && (
        <Card className={allUp ? 'border-success/40' : 'border-danger/40'}>
          <CardContent className="p-5 flex items-center gap-3">
            <span className={`inline-flex items-center justify-center w-10 h-10 rounded-[10px] ${allUp ? 'bg-success/15 text-success' : 'bg-danger/15 text-danger'}`}>
              <Activity className="w-5 h-5" />
            </span>
            <div>
              <div className="font-semibold text-foreground">
                {allUp ? 'All systems operational' : 'Service degradation detected'}
              </div>
              <div className="text-sm text-muted-foreground">
                {upCount} / {probedCount} up{downCount > 0 && <> · {downCount} down</>}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="py-12 flex items-center justify-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Checking services…
        </div>
      ) : services.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">No service data.</div>
      ) : (
        <div className="space-y-6">
          {groups.map(({ cat, items }) => (
            <div key={cat} className="space-y-3">
              <div className="flex items-center gap-2 text-label uppercase text-muted-foreground">
                {CATEGORY_META[cat]?.icon}
                {CATEGORY_META[cat]?.title ?? cat}
                <span className="text-muted-foreground/70">({items.length})</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((s) => {
                  const unknown = s.status === 'unknown';
                  const up = s.status === 'up';
                  return (
                    <Card key={s.name}>
                      <CardContent className="p-5">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex items-center justify-center w-10 h-10 rounded-[10px] bg-muted text-muted-foreground">
                            <Server className="w-5 h-5" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-foreground flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${dot(s.status)}`} />
                              {s.name}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {unknown ? 'not monitored' : `${s.responseMs} ms`}
                              {s.version && <span title={s.version}> · v {shortVer(s.version)}</span>}
                            </div>
                          </div>
                          <Badge
                            className={
                              unknown
                                ? 'bg-muted text-muted-foreground'
                                : up
                                  ? 'bg-success/15 text-success'
                                  : 'bg-danger/15 text-danger'
                            }
                          >
                            {unknown ? <HelpCircle className="w-3 h-3 mr-1" /> : up ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                            {unknown ? 'Unknown' : up ? 'Up' : 'Down'}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MonitoringPage;
